/**
 * Navigation Drawer Component
 * Desktop-only permanent sidebar in collapsed (icon-only) mode.
 * On mobile/tablet, primary navigation is handled by the BottomNav component.
 */

import { Drawer, Box, useTheme, useMediaQuery, Toolbar } from '@mui/material';
import { NavList } from './NavList';

// Drawer width — always collapsed
const DRAWER_WIDTH_COLLAPSED = 72;

/**
 * Navigation Drawer Component
 * Permanent collapsed sidebar on desktop (lg+). Returns null on smaller screens.
 * Workspace children are accessible via flyout popover (handled by NavItem).
 */
export const NavigationDrawer: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  if (!isDesktop) {
    return null;
  }

  return (
    <Drawer
      variant="permanent"
      open
      sx={{
        width: DRAWER_WIDTH_COLLAPSED,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH_COLLAPSED,
          boxSizing: 'border-box',
          overflowX: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        }}
      >
        {/* Spacer for AppBar */}
        <Toolbar sx={{ minHeight: { xs: 56, md: 64 } }} />

        {/* Navigation Items */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <NavList collapsed={true} />
        </Box>
      </Box>
    </Drawer>
  );
};

// Export drawer width for use in layout
export { DRAWER_WIDTH_COLLAPSED };
// Keep DRAWER_WIDTH_EXPANDED export for backward compatibility (barrel export)
const DRAWER_WIDTH_EXPANDED = 256;
export { DRAWER_WIDTH_EXPANDED };
